import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Loader2, Package, Check } from "lucide-react";
import { DashShell, StatCard, Badge } from "@/components/dash/DashShell";
import { api, inr } from "@/lib/api";
import { ShoppingBag, Activity, UserCheck } from "lucide-react";

const NAV = [{ to: "/account", label: "My Dashboard", icon: LayoutDashboard }];
const STAGES = ["Order Received", "Requirement Discussion", "Information Received", "Work Started", "First Draft", "Revision", "Completed"];

export default function CustomerDashboard() {
    const nav = useNavigate();
    const [orders, setOrders] = useState(null);
    useEffect(() => { api.get("/customer/orders").then(({ data }) => setOrders(data)); }, []);

    if (!orders) return <DashShell nav={NAV} title="My Dashboard"><div className="grid place-items-center py-32 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div></DashShell>;

    const active = orders.filter((o) => o.order_status !== "Completed").length;
    const completed = orders.filter((o) => o.order_status === "Completed").length;

    return (
        <DashShell nav={NAV} title="My Dashboard">
            <div className="flex flex-wrap gap-2 mb-6">
                <Link to="/account/brand" className="btn-ghost-cream !border-teal-900/20 !text-teal-900 rounded-full px-4 py-2 text-sm" data-testid="cust-link-brand">My Brand Profile</Link>
                <Link to="/messages" className="btn-ghost-cream !border-teal-900/20 !text-teal-900 rounded-full px-4 py-2 text-sm" data-testid="cust-link-messages">Messages</Link>
                <Link to="/opportunities" className="btn-ghost-cream !border-teal-900/20 !text-teal-900 rounded-full px-4 py-2 text-sm" data-testid="cust-link-opps">Browse Opportunities</Link>
            </div>
            {orders.length === 0 ? (
                <div className="bg-white rounded-2xl border border-black/5 p-10 text-center shadow-sm">
                    <Package className="w-12 h-12 text-teal-900/30 mx-auto mb-4" />
                    <h3 className="font-semibold text-teal-900 text-lg">No orders yet</h3>
                    <p className="text-teal-900/60 text-sm mt-2">Explore our services to start your digital growth journey.</p>
                    <button onClick={() => nav("/services")} className="btn-gold rounded-full px-6 py-2.5 text-sm mt-5">Explore Services</button>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                        <StatCard label="My Orders" value={orders.length} icon={ShoppingBag} testid="cust-stat-orders" />
                        <StatCard label="Active Projects" value={active} icon={Activity} accent="gold" />
                        <StatCard label="Completed" value={completed} icon={Check} accent="green" />
                    </div>

                    <div className="space-y-4">
                        {orders.map((o) => {
                            const idx = Math.max(0, STAGES.indexOf(o.order_status));
                            return (
                                <div key={o.order_id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid={`cust-order-${o.order_id}`}>
                                    <div className="flex flex-wrap justify-between items-start gap-3">
                                        <div>
                                            <div className="font-mono text-xs text-teal-700">{o.order_id}</div>
                                            <div className="font-semibold text-teal-900 mt-1">{[o.package, o.service].filter(Boolean).join(", ")}</div>
                                            <div className="text-sm text-teal-900/60 mt-1">{inr(o.amount)} · {o.assigned_bde_name ? `BDE: ${o.assigned_bde_name}` : "Awaiting BDE assignment"}</div>
                                        </div>
                                        <div className="flex gap-2"><Badge status={o.payment_status} /><Badge status={o.order_status} /></div>
                                    </div>
                                    <div className="mt-5 flex items-center gap-1 overflow-x-auto pb-2">
                                        {STAGES.map((s, i) => (
                                            <div key={s} className="flex items-center gap-1 shrink-0">
                                                <div className={`w-6 h-6 rounded-full grid place-items-center text-xs ${i <= idx ? "bg-growth text-teal-900" : "bg-teal-900/10 text-teal-900/40"}`}>{i <= idx ? <Check className="w-3.5 h-3.5" /> : i + 1}</div>
                                                <span className={`text-xs whitespace-nowrap ${i <= idx ? "text-teal-900 font-medium" : "text-teal-900/40"}`}>{s}</span>
                                                {i < STAGES.length - 1 && <div className={`w-6 h-px ${i < idx ? "bg-growth" : "bg-teal-900/10"}`} />}
                                            </div>
                                        ))}
                                    </div>
                                    {o.additional_requirements && <p className="mt-3 text-sm text-teal-900/60 bg-teal-900/5 rounded-lg p-3">{o.additional_requirements}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </DashShell>
    );
}
